import React, { useState, useEffect } from 'react';
import { useRAGStore } from '../store/ragStore';
import { FileText, Upload, Trash2, RefreshCw, Plus, X, Download } from 'lucide-react';

interface KeywordMapping {
  patterns: string[];
  replacement: string[];
}

interface KeywordMappingData {
  mappings: Record<string, KeywordMapping>;
}

const KeywordMappingTab: React.FC = () => {
  const {
    currentContext,
    uploadKeywordMappings,
    downloadKeywordMappings,
    refreshCurrentContext,
  } = useRAGStore();

  const [mappings, setMappings] = useState<Record<string, KeywordMapping>>({});
  const [newPatterns, setNewPatterns] = useState('');
  const [newReplacement, setNewReplacement] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (currentContext?.keywordMappings) {
      setMappings({ ...currentContext.keywordMappings });
    } else {
      setMappings({});
    }
  }, [currentContext]);

  const addMapping = async () => {
    const patterns = newPatterns.split(',').map(p => p.trim()).filter(p => p);
    const replacement = newReplacement.split(',').map(r => r.trim()).filter(r => r);

    if (patterns.length === 0 || replacement.length === 0) {
      alert('Please fill in patterns and replacement fields');
      return;
    }

    // Generate sequential key
    const existingKeys = Object.keys(mappings);
    const nextNumber = existingKeys.length + 1;
    const key = `mapping_${nextNumber}`;

    const newMappings = {
      ...mappings,
      [key]: {
        patterns: patterns || [],
        replacement: replacement || []
      }
    };

    setMappings(newMappings);
    setNewPatterns('');
    setNewReplacement('');

    // Auto-save after adding mapping
    try {
      await uploadKeywordMappings({ mappings: newMappings });
    } catch (error) {
      alert('Failed to save keyword mappings');
      // Revert the change if save failed
      setMappings({ ...mappings });
    }
  };

  const removeMapping = async (key: string) => {
    const newMappings = { ...mappings };
    delete newMappings[key];
    setMappings(newMappings);

    // Auto-save after removing mapping
    try {
      await uploadKeywordMappings({ mappings: newMappings });
    } catch (error) {
      alert('Failed to save keyword mappings');
      // Revert the change if save failed
      setMappings({ ...mappings });
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const text = await file.text();
      const data: KeywordMappingData = JSON.parse(text);
      
      if (!data.mappings || typeof data.mappings !== 'object') {
        alert('Invalid keyword mapping file format');
        return;
      }

      // Process the mappings to remove target_rags and intent_condition
      const processedMappings: Record<string, KeywordMapping> = {};
      for (const [key, mapping] of Object.entries(data.mappings)) {
        if (mapping && typeof mapping === 'object') {
          processedMappings[key] = {
            patterns: Array.isArray(mapping.patterns) ? mapping.patterns : [],
            replacement: Array.isArray(mapping.replacement) ? mapping.replacement : []
          };
        }
      }

      setMappings(processedMappings);
      await uploadKeywordMappings({ mappings: processedMappings });
      alert('Keyword mappings uploaded successfully');
    } catch (error) {
      alert('Failed to upload keyword mapping file');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMapping();
    }
  };

  const handleDownload = async () => {
    if (!currentContext) return;
    
    setIsDownloading(true);
    try {
      await downloadKeywordMappings();
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!currentContext) {
    return (
      <div className="card">
        <div className="text-center py-12">
          <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Context Selected
          </h3>
          <p className="text-gray-600">
            Please go to the Context Management tab to select a context before managing keyword mappings.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage Instructions */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <FileText className="h-5 w-5 text-blue-600 mt-0.5" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-blue-900 mb-3">Usage Instructions & Notes</h3>
            <div className="space-y-3 text-sm text-blue-800">
              <div>
                <h4 className="font-medium mb-2">📝 Input Format:</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li><strong>Patterns</strong>: Separate multiple patterns with English commas, supports regex</li>
                  <li><strong>Replacement</strong>: Separate multiple replacements with English commas, multiple replacements will be joined with spaces</li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">🌐 Multi-language Support:</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Fully supports Chinese, English, Japanese, and Korean languages</li>
                  <li>Supports mixed-language patterns, e.g.: <code className="bg-blue-100 px-1 rounded">求人情報,求人,仕事,招聘,job</code></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">⚠️ Special Character Handling:</h4>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Regex special characters need escaping: <code className="bg-blue-100 px-1 rounded">cat\\.ai</code> (dot escaped)</li>
                  <li>Use English comma <code className="bg-blue-100 px-1 rounded">,</code> for separation, not Chinese comma <code className="bg-blue-100 px-1 rounded">，</code></li>
                  <li>Pipe <code className="bg-blue-100 px-1 rounded">|</code> for OR logic: <code className="bg-blue-100 px-1 rounded">私|自分|僕</code></li>
                </ul>
              </div>
              
              <div>
                <h4 className="font-medium mb-2">💡 Example:</h4>
                <div className="bg-blue-100 p-3 rounded-lg text-xs">
                  <div><strong>Patterns:</strong> <code>cat\\.ai,catai,cat ai,CAT.AI</code></div>
                  <div><strong>Replacement:</strong> <code>CAT.AI,AI Company</code></div>
                  <div className="text-blue-600 mt-1">→ Result: When any pattern matches, replace with "CAT.AI AI Company"</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add New Mapping */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add New Keyword Mapping</h3>
          <button
            onClick={() => refreshCurrentContext()}
            className="btn-secondary flex items-center space-x-2"
            title="Refresh context data"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Patterns (comma-separated)
            </label>
            <input
              type="text"
              value={newPatterns}
              onChange={(e) => setNewPatterns(e.target.value)}
              onKeyPress={handleKeyPress}
              className="input-field"
              placeholder="Enter patterns separated by commas..."
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Replacement (comma-separated)
            </label>
            <input
              type="text"
              value={newReplacement}
              onChange={(e) => setNewReplacement(e.target.value)}
              onKeyPress={handleKeyPress}
              className="input-field"
              placeholder="Enter replacements separated by commas..."
            />
          </div>
          
          <button
            onClick={addMapping}
            disabled={!newPatterns.trim() || !newReplacement.trim()}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus className="h-4 w-4" />
            <span>Add Mapping</span>
          </button>
        </div>
      </div>

      {/* Mappings List */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Keyword Mappings</h3>
          <span className="text-sm text-gray-500">
            {Object.keys(mappings).length} mapping(s)
          </span>
        </div>
        
        {Object.keys(mappings).length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No keyword mappings defined</p>
            <p className="text-sm text-gray-400">Add mappings above or upload a file to get started</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(mappings).map(([key, mapping]) => (
              <div
                key={key}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <span className="font-medium text-gray-900">{key}</span>
                  </div>
                  
                  <button
                    onClick={() => removeMapping(key)}
                    className="text-red-400 hover:text-red-600 transition-colors duration-200"
                    title="Remove mapping"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-700">Patterns:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(mapping.patterns || []).map((pattern, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                        >
                          {pattern}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm font-medium text-gray-700">Replacement:</span>
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(mapping.replacement || []).map((rep, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded"
                        >
                          {rep}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Current Context Info */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Current Context</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-700">Context ID</p>
            <p className="text-gray-900">{currentContext.contextId}</p>
          </div>
          
          <div>
            <p className="text-sm font-medium text-gray-700">Current Mappings</p>
            <p className="text-gray-900">{Object.keys(mappings).length}</p>
          </div>
        </div>
      </div>

      {/* File Upload - Moved to bottom */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Keyword Mapping File</h3>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <input
              type="file"
              accept=".json"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="input-field flex-1"
            />
            <Upload className={`h-4 w-4 ${isUploading ? 'animate-spin' : ''}`} />
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Upload a JSON file with keyword mappings. The file should contain:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li><code>patterns</code>: Array of regex patterns to match</li>
              <li><code>replacement</code>: Array of replacement strings</li>
              <li><code>target_rags</code> and <code>intent_condition</code> will be automatically removed</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Download Keyword Mappings */}
      <div className="card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Download Keyword Mappings</h3>
        
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="btn-primary flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className={`h-4 w-4 ${isDownloading ? 'animate-spin' : ''}`} />
              <span>{isDownloading ? 'Downloading...' : 'Download JSON'}</span>
            </button>
          </div>
          
          <div className="text-sm text-gray-600">
            <p>Download the current keyword mappings as a JSON file. If no keyword mapping file exists for this context, you will receive an error message.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeywordMappingTab;
